import EventBus from "../event-bus";
import ApiUtils from "../utils/apiUtils";
import { trackStartedCheckout } from "../utils/builderTracking";
import { getDraftIdFromContext } from "../utils/draftService";
import validationMixin from "./validationMixin";
import {
    captureCustomizerException,
    captureCustomizerMessage,
} from "../utils/sentry";
import { safeGetItem, safeGetJson } from "../utils/safeStorage";
import { getMetaTracking } from "../utils/metaTracking";

export default {
    mixins: [validationMixin],
    data() {
        return {
            book: null,
            loading: false,
            upsellProductIds: [],
            additionalUpsellProducts: [],
        };
    },
    mounted() {
        EventBus.on("orderNow", this.orderNow);
        EventBus.on("orderNowDodoPayments", this.orderNowDodoPayments);
        EventBus.on("bookChanged", this.bookChanged);
        EventBus.on("upsellProductSelected", this.setUpsellProductId);
        EventBus.on("additionalUpsellProductsSelected", this.setAdditionalUpsellProductIds);
    },
    beforeUnmount() {
        EventBus.off("orderNow", this.orderNow);
        EventBus.off("orderNowDodoPayments", this.orderNowDodoPayments);
        EventBus.off("bookChanged", this.bookChanged);
        EventBus.off("upsellProductSelected", this.setUpsellProductId);
        EventBus.off("additionalUpsellProductsSelected", this.setAdditionalUpsellProductIds);
    },
    methods: {
        bookChanged(book) {
            this.book = book;
        },
        setUpsellProductId(productIds) {
            this.upsellProductIds = this.normalizeProductIds(productIds);
        },
        setAdditionalUpsellProductIds(productIds) {
            this.additionalUpsellProducts = this.normalizeProductItems(productIds);
        },
        normalizeProductIds(productIds) {
            const ids = Array.isArray(productIds) ? productIds : [productIds];

            return [
                ...new Set(
                    ids.flatMap((id) => this.getProductIdsFromValue(id))
                ),
            ];
        },
        normalizeProductItems(products) {
            const productItems = Array.isArray(products) ? products : [products];
            const productsById = {};

            productItems.forEach((product) => {
                const ids = this.getProductIdsFromValue(
                    product?.id ||
                    product?.variantId ||
                    product?.productId ||
                    product?.productIds ||
                    product
                );
                const quantity = this.normalizeQuantity(product?.quantity || 1);

                ids.forEach((id) => {
                    const currentQuantity = productsById[id]?.quantity || 0;

                    productsById[id] = {
                        id,
                        quantity: Math.max(currentQuantity, quantity),
                    };
                });
            });

            return Object.values(productsById);
        },
        getProductIdsFromValue(value) {
            if (value === undefined || value === null) {
                return [];
            }

            return String(value)
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean);
        },
        normalizeQuantity(quantity) {
            const parsedQuantity = Number.parseInt(quantity, 10);

            return Number.isFinite(parsedQuantity) && parsedQuantity > 0
                ? parsedQuantity
                : 1;
        },
        getSelectedUpsellProducts(variantId) {
            const bundleProducts = this.upsellProductIds.map((id) => {
                return {
                    id,
                    quantity: 1,
                };
            });

            return this.normalizeProductItems([
                ...bundleProducts,
                ...this.additionalUpsellProducts,
            ]).filter((product) => product.id.toString() !== variantId.toString());
        },
        orderNow() {
            if (this.loading) {
                return;
            }

            const variantId = this.pageSettings.variant || "";
            if (!variantId) {
                console.error("Variant ID is missing");
                this.captureCheckoutMessage(
                    "checkout_variant_missing",
                    "orderNow",
                    variantId,
                    {
                        reason: "page_settings_variant_missing",
                    }
                );
                return;
            }

            this.checkCartAndProceed(variantId);
        },

        orderNowDodoPayments() {
            if (this.loading) {
                return;
            }

            const variantId = this.pageSettings.variant || "";
            if (!variantId) {
                console.error("Variant ID is missing");
                this.captureCheckoutMessage(
                    "checkout_variant_missing",
                    "orderNowDodoPayments",
                    variantId,
                    {
                        reason: "page_settings_variant_missing",
                    }
                );
                return;
            }

            this.proceedToDodoCheckout(variantId);
        },

        getCheckoutFlow(action) {
            return action === "orderNowDodoPayments" ? "dodo" : "shopify";
        },

        getCartItemVariantIds(items) {
            return (items || [])
                .filter((item) => item && item.id)
                .map((item) => String(item.id));
        },

        getCheckoutErrorContext(error) {
            return {
                errorName: error?.name || null,
                errorMessage: error?.message || null,
                responseStatus: error?.response?.status || null,
                responseStatusText: error?.response?.statusText || null,
                requestUrl: error?.config?.url || null,
                requestMethod: error?.config?.method || null,
                isAxiosError: Boolean(error?.isAxiosError),
            };
        },

        getCheckoutSentryTags(action, variantId, extraTags = {}) {
            return {
                component: this.$options.name,
                action,
                checkout_flow: this.getCheckoutFlow(action),
                variant: variantId || this.pageSettings?.variant || "unknown",
                host: window.location.host,
                ...extraTags,
            };
        },

        getCheckoutSentryContext(variantId, extraContext = {}) {
            return {
                variant: variantId || this.pageSettings?.variant || null,
                product: this.pageSettings?.product || null,
                path: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                language: document.documentElement.lang || null,
                market: this.getCurrentMarket(),
                currency: this.getCurrentCurrency(),
                ...extraContext,
            };
        },

        captureCheckoutMessage(message, action, variantId, context = {}, level = "error", tags = {}) {
            captureCustomizerMessage(message, {
                level,
                tags: this.getCheckoutSentryTags(action, variantId, {
                    checkout_issue: message,
                    ...tags,
                }),
                context: this.getCheckoutSentryContext(variantId, {
                    checkoutIssue: message,
                    ...context,
                }),
            });
        },

        captureCheckoutException(error, message, action, variantId, context = {}, tags = {}) {
            captureCustomizerException(error, {
                tags: this.getCheckoutSentryTags(action, variantId, {
                    checkout_issue: message,
                    ...tags,
                }),
                context: this.getCheckoutSentryContext(variantId, {
                    checkoutIssue: message,
                    ...this.getCheckoutErrorContext(error),
                    ...context,
                }),
            });
        },

        checkCartAndProceed(variantId) {
            const items = this.prepareCartItems(variantId);
            if (!items || items.length === 0) {
                return;
            }
            
            ApiUtils.getCart()
            .then((cart) => {
                
                const cartItems = Array.isArray(cart?.items) ? cart.items : [];

                if (!Array.isArray(cart?.items)) {
                    this.captureCheckoutMessage(
                        "shopify_cart_preflight_cart_invalid",
                        "orderNow",
                        variantId,
                        {
                            fallback: "add_items_to_cart",
                            itemCount: items.length,
                            itemVariantIds: this.getCartItemVariantIds(items),
                            cartType: cart === null
                                ? "null"
                                : Array.isArray(cart)
                                    ? "array"
                                    : typeof cart,
                            cartKeys: cart && typeof cart === "object"
                                ? Object.keys(cart).slice(0, 20)
                                : [],
                            hasCartItems: Boolean(cart?.items),
                        },
                        "warning",
                        {
                            checkout_preflight_fallback: "invalid_cart_response",
                        }
                    );
                }
                
                const itemsToRemove = {};
                let needsRemoval = false;
                
                const newItemIds = items.map(item => item.id.toString());
                
                cartItems.forEach(cartItem => {
                    if (!cartItem?.key || !cartItem?.variant_id) {
                        return;
                    }

                    const cartItemId = cartItem.variant_id.toString();
                    
                    const isDuplicate = newItemIds.includes(cartItemId);
                    
                    const isAssociatedUpsell = cartItem.properties && 
                                              cartItem.properties._associatedWithVariant === variantId.toString();
                    
                    if (isDuplicate || isAssociatedUpsell) {
                        itemsToRemove[cartItem.key] = 0;
                        needsRemoval = true;
                    }
                });
                
                if (needsRemoval) {
                    return ApiUtils.updateCart(itemsToRemove)
                    .then(() => {
                        this.addItemsToCart(items);
                    });
                } else {
                    this.addItemsToCart(items);
                }
            })
            .catch((error) => {
                if (ApiUtils.isDevMode()) {
                    console.error("Error checking cart", ApiUtils.getErrorMetadata(error));
                }
                this.captureCheckoutException(
                    error,
                    "shopify_cart_preflight_failed",
                    "orderNow",
                    variantId,
                    {
                        fallback: "add_items_to_cart",
                        itemCount: items.length,
                        itemVariantIds: this.getCartItemVariantIds(items),
                    }
                );
                this.addItemsToCart(items);
            });
        },
        
        prepareCartItems(variantId, action = "orderNow") {
            const incompleteStep = this.getIncompletePreviousStep();
            if (incompleteStep) {
                this.captureCheckoutMessage(
                    "checkout_payload_missing_required_values",
                    action,
                    variantId,
                    {
                        requiredValues: [`${incompleteStep}Values`],
                        reason: "required_field_empty",
                    }
                );
                const stepUrl = this.pageSettings[`${incompleteStep}Url`];
                if (stepUrl && this.$router) {
                    this.$router.push(stepUrl);
                }
                return null;
            }

            if (this.$options.name === "StepThree" && 
                this.pageSettings.step3StorageKey &&
                this.pageSettings.step3StorageKey !== ""
            ) {
                const step3ValuesObj = safeGetJson(
                    this.pageSettings.step3StorageKey,
                    {}
                );

                const shouldPreventNavigation =
                    this.checkValues(step3ValuesObj);
                if (shouldPreventNavigation) {
                    EventBus.emit("showMessageBox", "stepThreeMessageBox");
                    EventBus.emit("throwErrorForRequiredInputs", true);
                    return null;
                }

                EventBus.emit("hideMessageBox", "stepThreeMessageBox");
                EventBus.emit("throwErrorForRequiredInputs", false);
            }

            const step1Values = this.pageSettings.step1StorageKey ? 
                (safeGetItem(this.pageSettings.step1StorageKey, "") || "") : "";
            const step2Values = this.pageSettings.step2StorageKey ? 
                (safeGetItem(this.pageSettings.step2StorageKey, "") || "") : "";
            const step3Values = this.pageSettings.step3StorageKey ? 
                (safeGetItem(this.pageSettings.step3StorageKey, "") || "") : "";

            const draftId = getDraftIdFromContext();
            let propBook = "";
            if (typeof this.book === "object") {
                const bookPayload = this.book && draftId
                    ? { ...this.book, draftId }
                    : this.book;
                propBook = JSON.stringify(bookPayload);
            } else {
                propBook = this.book;
            }

            const properties = {
                _step1Values: step1Values,
                _step2Values: step2Values,
                _step3Values: step3Values,
                _book: propBook,
                _variantId: variantId,
                _product: this.pageSettings.product,
            };

            if (draftId) {
                properties._draftId = draftId;
            }

            const requiredValidations = [];
            
            if (this.pageSettings.step1StorageKey && properties._step1Values === "") {
                requiredValidations.push("step1Values");
            }
            
            
            if (properties._book === "") {
                requiredValidations.push("book");
            }

            if (requiredValidations.length > 0) {
                if (ApiUtils.isDevMode()) {
                    return null;
                }

                captureCustomizerMessage("checkout_payload_missing_required_values", {
                    level: "error",
                    tags: {
                        component: this.$options.name,
                        action,
                        variant: variantId,
                        host: window.location.host,
                    },
                    context: {
                        requiredValues: requiredValidations,
                        hasStep1Values: Boolean(properties._step1Values),
                        hasStep2Values: Boolean(properties._step2Values),
                        hasStep3Values: Boolean(properties._step3Values),
                        hasBook: Boolean(properties._book),
                        hasDraftId: Boolean(draftId),
                    },
                });

                return null;
            }

            let items = [
                {
                    id: variantId,
                    quantity: 1,
                    properties: properties,
                },
            ];

            const milestonesProductCountKey =
                "milestonesProductCount_" + variantId;
            const milestonesShopifyProductIdKey =
                "milestonesShopifyProductId_" + variantId;

            const milestonesProductCount =
                safeGetItem(milestonesProductCountKey, 0) || 0;
            const milestonesShopifyProductId =
                safeGetItem(milestonesShopifyProductIdKey, "") || "";

            if (milestonesProductCount > 0) {
                if (milestonesShopifyProductId === "") {
                    console.error(
                        "Milestones Shopify Product ID is missing but product count is greater than 0"
                    );
                    this.captureCheckoutMessage(
                        "checkout_milestones_variant_missing",
                        action,
                        variantId,
                        {
                            milestonesProductCount,
                            hasMilestonesShopifyProductId: false,
                        }
                    );
                    return null;
                }

                items.push({
                    id: milestonesShopifyProductId,
                    quantity: milestonesProductCount,
                });
            }

            const upsellProducts = this.getSelectedUpsellProducts(variantId);

            if (upsellProducts.length > 0) {
                
                for (const upsellProduct of upsellProducts) {
                    items.push({
                        id: upsellProduct.id,
                        quantity: upsellProduct.quantity,
                        properties: {
                            _associatedWithVariant: variantId.toString()
                        }
                    });
                }
                
            }
            
            return items;
        },

        /**
         * Earlier steps must still satisfy their required fields at checkout,
         * also on draft links where the step redirect is skipped.
         */
        getIncompletePreviousStep() {
            const previousSteps = {
                StepTwo: ["step1"],
                StepThree: ["step1", "step2"],
            }[this.$options.name] || [];

            return previousSteps.find((step) => {
                const storageKey = this.pageSettings?.[`${step}StorageKey`];
                return storageKey && this.checkValues(safeGetJson(storageKey, {}));
            }) || null;
        },

        proceedToDodoCheckout(variantId) {
            const items = this.prepareCartItems(variantId, "orderNowDodoPayments");
            if (!items || items.length === 0) {
                return;
            }

            const draftId = getDraftIdFromContext();
            const payload = this.buildDodoCheckoutPayload(variantId, items, draftId);

            this.loading = true;

            ApiUtils.createDodoCheckout(payload)
                .then(async (data) => {
                    if (!data || !data.success || !data.checkout_url) {
                        if (ApiUtils.isDevMode()) {
                            console.error("Dodo checkout failed", {
                                responseSuccess: Boolean(data?.success),
                                hasCheckoutUrl: Boolean(data?.checkout_url),
                            });
                        }
                        this.captureCheckoutMessage(
                            "dodo_checkout_response_invalid",
                            "orderNowDodoPayments",
                            variantId,
                            {
                                itemCount: items.length,
                                itemVariantIds: this.getCartItemVariantIds(items),
                                responseSuccess: Boolean(data?.success),
                                hasCheckoutUrl: Boolean(data?.checkout_url),
                                responseKeys: data ? Object.keys(data).slice(0, 20) : [],
                            }
                        );
                        return;
                    }

                    await trackStartedCheckout({
                        vm: this,
                        content: this.content,
                        items,
                        checkoutFlow: "dodo",
                    });
                    window.location.href = data.checkout_url;
                })
                .catch((error) => {
                    if (ApiUtils.isDevMode()) {
                        console.error("Error creating Dodo checkout", ApiUtils.getErrorMetadata(error));
                    }
                    this.captureCheckoutException(
                        error,
                        "dodo_checkout_request_failed",
                        "orderNowDodoPayments",
                        variantId,
                        {
                            itemCount: items.length,
                            itemVariantIds: this.getCartItemVariantIds(items),
                            hasDraftId: Boolean(draftId),
                        }
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        buildDodoCheckoutPayload(variantId, items, draftId) {
            const lineItems = this.buildDodoLineItems(items);
            const productTitle = this.pageSettings.product ? String(this.pageSettings.product) : null;
            const handle = this.getProductHandleFromUrl();
            const metaTracking = getMetaTracking();

            return {
                productType: "song",
                variantId: String(variantId),
                productTitle,
                handle,
                sourceProductUrl: window.location.href,
                language: document.documentElement.lang || "de",
                market: this.getCurrentMarket(),
                billingCurrency: this.getCurrentCurrency(),
                personalizationId: draftId || window.dreamytalesPersonalizationId || window.dreamytalesConfigId || null,
                lineItems,
                ...(metaTracking ? { metaTracking } : {}),
            };
        },

        buildDodoLineItems(items) {
            return (items || [])
                .filter((item) => item && item.id)
                .map((item) => {
                    const quantity = Number(item.quantity);

                    return {
                        variantId: String(item.id),
                        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                        lineItemProperties: this.buildDodoLineItemProperties(item.properties),
                    };
                });
        },

        buildDodoLineItemProperties(properties) {
            const lineItemProperties = {};

            Object.entries(properties || {}).forEach(([key, value]) => {
                if (value === undefined || value === null || value === "") {
                    return;
                }

                if (["string", "number", "boolean"].includes(typeof value)) {
                    lineItemProperties[key] = String(value);
                    return;
                }

                try {
                    lineItemProperties[key] = JSON.stringify(value);
                } catch (error) {
                    // The value is dropped from the order, so the order would be missing data.
                    captureCustomizerException(error, {
                        tags: {
                            component: "cartMixin",
                            failure_reason: "line_item_property_not_serializable",
                            property_key: String(key).slice(0, 60),
                        },
                    });
                }
            });

            return lineItemProperties;
        },

        getProductHandleFromUrl() {
            const productPathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
            if (productPathMatch) {
                return productPathMatch[1];
            }

            const referrerProductPathMatch = document.referrer?.match(/\/products\/([^/?#]+)/);
            if (referrerProductPathMatch) {
                return referrerProductPathMatch[1];
            }

            return null;
        },

        getCurrentMarket() {
            return window.Shopify?.country || null;
        },

        getCurrentCurrency() {
            return window.Shopify?.currency?.active || "EUR";
        },
        
        addItemsToCart(items) {
            this.loading = true;
            
            ApiUtils.addToCart(items)
                .then(async () => {
                    await trackStartedCheckout({
                        vm: this,
                        content: this.content,
                        items,
                        checkoutFlow: "shopify",
                    });
                    ApiUtils.redirectToCheckout();
                })
                .catch((error) => {
                    if (ApiUtils.isDevMode()) {
                        console.error("Error adding item to cart", ApiUtils.getErrorMetadata(error));
                    }
                    const variantId = items?.[0]?.id || this.pageSettings.variant || "";
                    this.captureCheckoutException(
                        error,
                        "shopify_cart_add_failed",
                        "orderNow",
                        variantId,
                        {
                            itemCount: items?.length || 0,
                            itemVariantIds: this.getCartItemVariantIds(items),
                        }
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
}; 
